// src/components/device/settings/EditStatsTab.tsx

import { useEffect, useMemo, useState, useRef } from "react";
import { Alert, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import http from "services/httpClient";

import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

interface Label {
  id: number;
  label: string;
  unit: string;
  column_index: number;
  is_enabled: boolean;
}

type Aggregation = "min" | "max" | "avg";
type WindowHours = 1 | 6 | 8 | 12 | 24;

interface StatDefinition {
  id: number;
  device_id: number;
  source_column_index: number;
  aggregation: Aggregation;
  window_hours: WindowHours;
  result_label: string;
  unit: string | null;
  sort_order: number;
}

interface Props {
  deviceId: number;
}

const WINDOW_OPTIONS: WindowHours[] = [1, 6, 8, 12, 24];
const AGGREGATION_OPTIONS: Aggregation[] = ["min", "max", "avg"];

export function EditStatsTab({ deviceId }: Props) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [stats, setStats] = useState<StatDefinition[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);

  const [sourceColumnIndex, setSourceColumnIndex] = useState<number | "">("");
  const [aggregation, setAggregation] = useState<Aggregation>("avg");
  const [windowHours, setWindowHours] = useState<WindowHours>(12);
  const [resultLabel, setResultLabel] = useState("");
  const [unit, setUnit] = useState("");

  const isFirstLoad = useRef(true);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enabledLabels = useMemo(
    () => labels.filter((l) => l.is_enabled),
    [labels]
  );

  const selectedLabel = useMemo(
    () =>
      enabledLabels.find((l) => l.column_index === Number(sourceColumnIndex)) ??
      null,
    [enabledLabels, sourceColumnIndex]
  );

  const resetForm = () => {
    setEditingId(null);
    setSourceColumnIndex(enabledLabels[0]?.column_index ?? "");
    setAggregation("avg");
    setWindowHours(12);
    setResultLabel("");
    setUnit("");
  };

  const buildDefaultResultLabel = (
    agg: Aggregation,
    hrs: WindowHours,
    label?: string
  ) => {
    const aggText = agg.toUpperCase();
    return label ? `${aggText}${hrs} ${label}` : `${aggText}${hrs}`;
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);

    try {
      const [labelsRes, statsRes] = await Promise.all([
        http.get(`/device/${deviceId}/labels`),
        http.get(`/stat-definitions/device/${deviceId}`),
      ]);

      const labelData: Label[] = (labelsRes.data || []).filter(
        (l: Label) => l.is_enabled
      );
      const statData: StatDefinition[] = statsRes.data || [];

      setLabels(labelsRes.data || []);
      setStats(statData);

      if (labelData.length > 0) {
        setSourceColumnIndex(labelData[0].column_index);
        setResultLabel(buildDefaultResultLabel("avg", 12, labelData[0].label));
        setUnit(labelData[0].unit || "");
      } else {
        setSourceColumnIndex("");
        setResultLabel("");
        setUnit("");
      }
    } catch (err) {
      setError("Failed to load stat definitions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!deviceId) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // ✅ DRAG HANDLER
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = stats.findIndex((s) => s.id === active.id);
    const newIndex = stats.findIndex((s) => s.id === over.id);

    setStats(arrayMove(stats, oldIndex, newIndex));
  };

  // ✅ SAVE ORDER
  const saveOrder = async (updated: StatDefinition[]) => {
    setSaving(true);
    try {
      const payload = updated.map((item, index) => ({
        id: item.id,
        sort_order: index + 1,
      }));

      await http.post("/stat-definitions/reorder", payload);
    } finally {
      setSaving(false);
    }
  };

  // ✅ AUTO SAVE
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      saveOrder(stats);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [stats]);

  useEffect(() => {
    if (editingId !== null) return;
    if (!selectedLabel) return;

    setResultLabel((prev) =>
      prev.trim()
        ? prev
        : buildDefaultResultLabel(aggregation, windowHours, selectedLabel.label)
    );

    setUnit((prev) => (prev.trim() ? prev : selectedLabel.unit || ""));
  }, [selectedLabel, aggregation, windowHours, editingId]);

  const handleSourceChange = (value: string) => {
    const next = Number(value);
    setSourceColumnIndex(next);

    if (editingId !== null) return;

    const label = enabledLabels.find((l) => l.column_index === next);
    setResultLabel(buildDefaultResultLabel(aggregation, windowHours, label?.label));
    setUnit(label?.unit || "");
  };

  const handleAggregationChange = (value: Aggregation) => {
    setAggregation(value);

    if (editingId !== null) return;

    setResultLabel(buildDefaultResultLabel(value, windowHours, selectedLabel?.label));
  };

  const handleWindowChange = (value: WindowHours) => {
    setWindowHours(value);

    if (editingId !== null) return;

    setResultLabel(buildDefaultResultLabel(aggregation, value, selectedLabel?.label));
  };

  const handleSubmit = async () => {
    if (sourceColumnIndex === "") {
      setError("Please select a parameter");
      return;
    }

    if (!resultLabel.trim()) {
      setError("Please enter a result label");
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      device_id: deviceId,
      source_column_index: Number(sourceColumnIndex),
      aggregation,
      window_hours: windowHours,
      result_label: resultLabel.trim(),
      unit: unit.trim() || null,
    };

    try {
      if (editingId !== null) {
        await http.patch(`/stat-definitions/${editingId}`, {
          source_column_index: payload.source_column_index,
          aggregation: payload.aggregation,
          window_hours: payload.window_hours,
          result_label: payload.result_label,
          unit: payload.unit,
        });
      } else {
        await http.post(`/stat-definitions`, payload);
      }

      await loadAll();
      resetForm();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError("Duplicate stat definition already exists");
      } else {
        setError("Failed to save stat definition");
      }
    } finally {
      setSaving(false);
    }
  };

  // const handleEdit = (row: StatDefinition) => {
  //   setEditingId(row.id);
  //   setSourceColumnIndex(row.source_column_index);
  //   setAggregation(row.aggregation);
  //   setWindowHours(row.window_hours);
  //   setResultLabel(row.result_label);
  //   setUnit(row.unit || "");
  //   setError(null);
  // };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setError(null);

    try {
      await http.delete(`/stat-definitions/${id}`);
      await loadAll();

      if (editingId === id) {
        resetForm();
      }
    } catch {
      setError("Failed to delete stat definition");
    } finally {
      setDeletingId(null);
    }
  };

  const getSourceDisplay = (columnIndex: number) => {
    const label = labels.find((l) => l.column_index === columnIndex);
    if (!label) return `x${columnIndex}`;
    return `${label.label} [x${columnIndex}]`;
  };

  // ✅ SORTABLE ROW
  const SortableRow = ({ row }: { row: StatDefinition }) => {
    const { setNodeRef, transform, transition, attributes, listeners } =
      useSortable({ id: row.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <tr ref={setNodeRef} style={style}>
        <td {...attributes} {...listeners} style={{ cursor: "grab" }}>⠿</td>
        <td>{getSourceDisplay(row.source_column_index)}</td>
        <td>{row.aggregation.toUpperCase()}</td>
        <td>{row.window_hours}h</td>
        <td>{row.result_label}</td>
        <td>{row.unit || "-"}</td>
        <td>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDelete(row.id)}
            disabled={deletingId === row.id}
          >
            {deletingId === row.id ? "Deleting..." : "Delete"}
          </Button>
        </td>
      </tr>
    );
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div>
      <h5 className="mb-3">Stats Definitions</h5>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Source Parameter</Form.Label>
            <Form.Select
              value={sourceColumnIndex}
              onChange={(e) => handleSourceChange(e.target.value)}
            >
              {enabledLabels.length === 0 && (
                <option value="">No enabled parameters</option>
              )}

              {enabledLabels.map((label) => (
                <option key={label.id} value={label.column_index}>
                  {label.label} ({label.unit || "-"}) [x{label.column_index}]
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Aggregation</Form.Label>
            <Form.Select
              value={aggregation}
              onChange={(e) =>
                handleAggregationChange(e.target.value as Aggregation)
              }
            >
              {AGGREGATION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Window</Form.Label>
            <Form.Select
              value={windowHours}
              onChange={(e) =>
                handleWindowChange(Number(e.target.value) as WindowHours)
              }
            >
              {WINDOW_OPTIONS.map((hrs) => (
                <option key={hrs} value={hrs}>
                  {hrs}h
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Unit</Form.Label>
            <Form.Control
              type="text"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="Unit"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="d-flex align-items-end">
          <Button
            variant={editingId !== null ? "primary" : "success"}
            onClick={handleSubmit}
            disabled={saving || sourceColumnIndex === ""}
            className="w-100"
          >
            {saving ? "Saving..." : editingId !== null ? "Update" : "Add"}
          </Button>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={10}>
          <Form.Group>
            <Form.Label>Result Label</Form.Label>
            <Form.Control
              type="text"
              value={resultLabel}
              onChange={(e) => setResultLabel(e.target.value)}
              placeholder="e.g., AVG12 Carbon"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="d-flex align-items-end">
          <Button
            variant="outline-secondary"
            onClick={resetForm}
            className="w-100"
            disabled={saving}
          >
            Clear
          </Button>
        </Col>
      </Row>

      <h6 className="mb-3">Existing Stat Definitions</h6>

      {stats.length === 0 ? (
        <div className="text-muted">No stat definitions yet.</div>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={stats.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th></th>
                  <th>Source</th>
                  <th>Aggregation</th>
                  <th>Window</th>
                  <th>Result Label</th>
                  <th>Unit</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((row) => <SortableRow key={row.id} row={row} />)}
              </tbody>
            </Table>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export default EditStatsTab;